import type { AssignableResponse, CreateAssignmentRequest, ProgramAssignment, ProgramAssignmentsResponse, TrainingAssignmentResponse } from "@lms/shared-types";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { ApiRequestError, fetchJson } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { Button, DataTable, Dialog, DialogAlert, EmptyState, focusFallback, ErrorState, inputClass, labelClass, LoadingState, PageHeader, StatusPill, TextField } from "../../components/ui";
import { useAnnounce } from "../../lib/announce";
import { useFormat } from "../../lib/format";
import { dueFromDateField, durationWords, rosterRtiWords, ROSTER_WORD, withdrawable } from "../../lib/training";

/**
 * Program › Assignments (CNT-10, CNT-12, CNT-13; design.md §16 "Library,
 * curricula, assignments"): who has which course and by when, in the
 * caller's reach; *Assign a course* to one person or several; *Withdraw*
 * with a reason.
 *
 * The form offers only what `/v1/training/assignable` says this caller may
 * choose from, and one request goes per person, so each person's outcome
 * is its own: a duplicate for one does not stop the others, and the dialog
 * says who got it and who already had it. A workforce-training assignment
 * (no program, ADR-0018) is a choice made on purpose, and says it credits
 * no apprenticeship hours (CNT-26).
 */
const WORKFORCE = "";

/** Every mutation shows its error (qa-battery.md §1.1 rule 4): the API's words, with the request id to quote. */
function errorText(error: unknown): string {
  if (error instanceof ApiRequestError) return error.requestId ? `${error.message} (request ${error.requestId})` : error.message;
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong.";
}

export function AssignmentsPage() {
  const { session, config, me } = useAuth();
  const { date } = useFormat();
  const announce = useAnnounce();
  const [state, setState] = useState<{ data?: ProgramAssignmentsResponse; loading: boolean; error?: unknown }>({ loading: true });
  const [dialog, setDialog] = useState<{ kind: "assign" } | { kind: "withdraw"; assignment: ProgramAssignment } | null>(null);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  // After a withdrawal the row's Withdraw button — the dialog's opener — is gone once the list reloads, and focus with
  // it (Claude's walk of 2026-09-23, gairm/lms#236). The row's course takes it instead, once the reloaded list is in;
  // if the list could not be reloaded, the page's h1.
  const refocus = useRef<string | null>(null);
  useEffect(() => {
    const id = refocus.current;
    if (id === null || state.loading) return;
    refocus.current = null;
    const cell = state.data ? document.querySelector<HTMLElement>(`[data-course-for="${CSS.escape(id)}"]`) : null;
    if (cell) cell.focus();
    else focusFallback();
  }, [state]);

  useEffect(() => {
    if (!session) return;
    let live = true;
    fetchJson<ProgramAssignmentsResponse>(config, session, "/v1/training/assignments")
      .then((data) => live && setState({ data, loading: false }))
      .catch((error: unknown) => live && setState({ loading: false, error }));
    return () => {
      live = false;
    };
  }, [config, session, tick]);

  if (!session || !me) return null;
  const mayAssign = me.permissions.includes("assignment.create");
  const mayWithdraw = me.permissions.includes("assignment.withdraw");

  return (
    <>
      <PageHeader
        eyebrow="Program"
        title="Assignments"
        subtitle="Who has which course, and by when. Assign a course to one person or several; take one back with a reason."
        actions={
          mayAssign ? (
            <Button variant="primary" onClick={() => setDialog({ kind: "assign" })}>
              Assign a course
            </Button>
          ) : undefined
        }
      />
      {state.loading && <LoadingState label="Loading assignments…" />}
      {state.error !== undefined && <ErrorState title="The assignments could not be loaded." error={state.error} onRetry={reload} />}
      {state.data?.truncated && <p className="mb-3 text-sm text-muted">Showing the first 500. Filter to find the rest.</p>}
      {state.data && (
        <DataTable
          caption="Assignments"
          rows={state.data.assignments}
          rowKey={(a) => a.id}
          rowTestId={(a) => `assignment-${a.id}`}
          empty={<EmptyState title="Nothing assigned yet" hint={mayAssign ? "Assign a course and it appears here, with who has it and when it is wanted." : "When a course is assigned in your program it appears here."} />}
          columns={[
            { key: "person", header: "Person", render: (a) => (a.person.id === me.person.id ? `${a.person.displayName} (you)` : a.person.displayName), sortValue: (a) => a.person.displayName },
            {
              key: "course",
              header: "Course",
              // Focusable from script only: where focus goes when this row's Withdraw button leaves with the withdrawal.
              render: (a) => (
                <span tabIndex={-1} data-course-for={a.id}>
                  {a.item.title}
                </span>
              ),
              sortValue: (a) => a.item.title,
            },
            {
              key: "program",
              header: "Counts toward",
              // The program, and what the ledger says the course is worth there (CNT-26) — from the plan, never the item.
              render: (a) => (
                <span className="grid gap-0.5">
                  <span>{a.program?.name ?? <span className="text-muted">Workforce training</span>}</span>
                  {a.program && <span className="text-xs text-muted">{rosterRtiWords(a.rti)}</span>}
                </span>
              ),
              sortValue: (a) => a.program?.name ?? "",
            },
            {
              key: "due",
              header: "Wanted by",
              render: (a) =>
                a.dueAt ? (
                  <span className="flex flex-wrap items-center gap-2">
                    <time dateTime={a.dueAt}>{date(a.dueAt)}</time>
                    {a.overdue && <StatusPill tone="danger">Late</StatusPill>}
                  </span>
                ) : (
                  <span className="text-muted">No date</span>
                ),
              sortValue: (a) => a.dueAt ?? "9999",
            },
            { key: "status", header: "Status", render: (a) => <StatusPill tone={ROSTER_WORD[a.status].tone}>{ROSTER_WORD[a.status].word}</StatusPill>, sortValue: (a) => ROSTER_WORD[a.status].word },
            ...(mayWithdraw
              ? [
                  {
                    key: "actions",
                    header: "Actions",
                    render: (a: ProgramAssignment) =>
                      withdrawable(a) && a.managed ? (
                        <Button onClick={() => setDialog({ kind: "withdraw", assignment: a })}>
                          Withdraw<span className="sr-only"> {a.item.title} from {a.person.displayName}</span>
                        </Button>
                      ) : null,
                  },
                ]
              : []),
          ]}
        />
      )}
      {dialog?.kind === "assign" && (
        <AssignDialog
          onClose={() => setDialog(null)}
          onAssigned={(said) => {
            reload();
            if (said) announce(said);
          }}
        />
      )}
      {dialog?.kind === "withdraw" && (
        <WithdrawDialog
          assignment={dialog.assignment}
          onClose={() => setDialog(null)}
          onWithdrawn={(said) => {
            refocus.current = dialog.assignment.id;
            setDialog(null);
            reload();
            announce(said);
          }}
        />
      )}
    </>
  );
}

type Outcome = { personId: string; name: string; ok: true } | { personId: string; name: string; ok: false; error: unknown };

function AssignDialog({ onClose, onAssigned }: { onClose: () => void; onAssigned: (said?: string) => void }) {
  const { session, config } = useAuth();
  const [choices, setChoices] = useState<{ data?: AssignableResponse; error?: unknown }>({});
  const [itemId, setItemId] = useState("");
  const [people, setPeople] = useState<string[]>([]);
  const [programId, setProgramId] = useState<string | undefined>(undefined);
  const [due, setDue] = useState("");
  const [why, setWhy] = useState("");
  const [busy, setBusy] = useState(false);
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const courseId = useId();
  const programFieldId = useId();
  const whyId = useId();
  const whyDescId = useId();
  // The dialog focuses its first field when it opens, which is before the choices arrive; the course takes focus once it exists.
  const courseRef = useRef<HTMLSelectElement>(null);
  const loaded = choices.data !== undefined;
  useEffect(() => {
    if (loaded) courseRef.current?.focus();
  }, [loaded]);

  useEffect(() => {
    if (!session) return;
    let live = true;
    fetchJson<AssignableResponse>(config, session, "/v1/training/assignable")
      .then((data) => {
        if (!live) return;
        setChoices({ data });
        setItemId((current) => current || data.items[0]?.id || "");
      })
      .catch((error: unknown) => live && setChoices({ error }));
    return () => {
      live = false;
    };
  }, [config, session]);

  const data = choices.data;
  // The caller's first program unless they choose otherwise; workforce training only when chosen, or when there is no program.
  const program = programId ?? data?.programs[0]?.id ?? WORKFORCE;
  const item = data?.items.find((i) => i.id === itemId);
  const failed = outcomes.filter((o): o is Extract<Outcome, { ok: false }> => !o.ok);
  const done = outcomes.filter((o) => o.ok);

  const submit = async () => {
    if (!session || !data || !item || people.length === 0) return;
    setBusy(true);
    setOutcomes([]);
    const dueAt = dueFromDateField(due);
    const results: Outcome[] = [];
    for (const personId of people) {
      const name = data.people.find((p) => p.id === personId)?.displayName ?? personId;
      const body: CreateAssignmentRequest = { personId, contentItemId: item.id, ...(program ? { programId: program } : {}), ...(why.trim() ? { why: why.trim() } : {}), ...(dueAt ? { dueAt } : {}) };
      try {
        await fetchJson<TrainingAssignmentResponse>(config, session, "/v1/training/assignments", { method: "POST", body: JSON.stringify(body), headers: { "content-type": "application/json" } });
        results.push({ personId, name, ok: true });
      } catch (error) {
        results.push({ personId, name, ok: false, error });
      }
    }
    setBusy(false);
    const made = results.filter((r) => r.ok).map((r) => r.name);
    const refused = results.filter((r) => !r.ok);
    if (refused.length === 0) {
      onAssigned(`${item.title} assigned to ${made.join(", ")}.`);
      onClose();
      return;
    }
    // One channel for a partial result: the alert inside the dialog says who got it and who did not, so the live
    // region stays quiet rather than saying half of it a second time.
    if (made.length > 0) onAssigned();
    // Keep only the people it failed for, so trying again does not assign the others twice.
    setPeople(refused.map((r) => r.personId));
    setOutcomes(results);
  };

  return (
    <Dialog title="Assign a course" onClose={onClose}>
      {choices.error !== undefined && <ErrorState title="What you can assign could not be loaded." error={choices.error} />}
      {!data && choices.error === undefined && <LoadingState label="Loading courses and people…" />}
      {failed.length > 0 && (
        <DialogAlert focusKey={outcomes} className="mb-3 space-y-1 text-sm">
          {done.length > 0 && <p>Assigned to {done.map((o) => o.name).join(", ")}.</p>}
          <p className="font-medium text-danger">Not assigned:</p>
          <ul className="list-disc pl-5 text-danger">
            {failed.map((o) => (
              <li key={o.personId}>
                {o.name} — {o.error instanceof ApiRequestError && o.error.code === "ALREADY_ASSIGNED" ? "they already have this course, and have not finished it." : errorText(o.error)}
              </li>
            ))}
          </ul>
        </DialogAlert>
      )}
      {data && data.items.length === 0 && <EmptyState title="No published courses" hint="A course has to be published before it can be assigned." />}
      {data && data.items.length > 0 && (
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div className="grid gap-1.5">
            <label htmlFor={courseId} className={labelClass}>
              Course
            </label>
            <select ref={courseRef} id={courseId} className={`${inputClass} min-w-0 max-w-full`} value={itemId} onChange={(e) => setItemId(e.target.value)}>
              {data.items.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.title}
                </option>
              ))}
            </select>
            {item && <p className="text-sm text-muted">{[item.provider, durationWords(item.durationMinutes) ? `about ${durationWords(item.durationMinutes)}` : undefined, "RTI hours come from the program's plan, not the course"].filter(Boolean).join(" · ")}</p>}
          </div>

          <fieldset className="grid gap-2">
            <legend className={`${labelClass} mb-1`}>People</legend>
            {data.people.length === 0 ? (
              <p className="text-sm text-muted">Nobody is in your reach yet.</p>
            ) : (
              <ul className="grid gap-1">
                {data.people.map((p) => (
                  <li key={p.id} className="min-w-0">
                    <label className="flex min-h-11 items-center gap-3">
                      <input type="checkbox" className="size-5 shrink-0" checked={people.includes(p.id)} onChange={(e) => setPeople((now) => (e.target.checked ? [...now, p.id] : now.filter((x) => x !== p.id)))} />
                      <span className="min-w-0 break-words">{p.displayName}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </fieldset>

          <div className="grid gap-1.5">
            <label htmlFor={programFieldId} className={labelClass}>
              Counts toward
            </label>
            <select id={programFieldId} className={`${inputClass} min-w-0 max-w-full`} value={program} onChange={(e) => setProgramId(e.target.value)}>
              {data.programs.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
              <option value={WORKFORCE}>No program — workforce training</option>
            </select>
            {program === WORKFORCE && <p className="text-sm text-muted">Workforce training credits no apprenticeship hours, whatever the course is worth.</p>}
          </div>

          <TextField label="Wanted by (optional)" type="date" value={due} onChange={(e) => setDue(e.target.value)} />

          <div className="grid gap-1.5">
            <label htmlFor={whyId} className={labelClass}>
              Why (optional)
            </label>
            <p id={whyDescId} className="text-sm text-muted">
              The learner reads this on their training page — say it in their words.
            </p>
            <textarea id={whyId} aria-describedby={whyDescId} className={inputClass} rows={3} maxLength={500} value={why} onChange={(e) => setWhy(e.target.value)} />
          </div>

          <div>
            <Button type="submit" variant="primary" disabled={busy || !item || people.length === 0}>
              {busy ? "Assigning…" : people.length > 1 ? `Assign to ${people.length} people` : "Assign"}
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

function WithdrawDialog({ assignment: a, onClose, onWithdrawn }: { assignment: ProgramAssignment; onClose: () => void; onWithdrawn: (said: string) => void }) {
  const { session, config } = useAuth();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  const reasonId = useId();
  const reasonDescId = useId();
  return (
    <Dialog title={`Withdraw ${a.item.title} from ${a.person.displayName}?`} onClose={onClose} closeLabel="Keep it">
      {error !== undefined && (
        <DialogAlert focusKey={error} className="mb-3 text-danger">
          {errorText(error)}
        </DialogAlert>
      )}
      <p className="text-sm">It leaves their training list. Anything they have already done stays in the record, and the reason is kept with it.</p>
      <form
        className="mt-4 grid gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!session || !reason.trim()) return;
          setBusy(true);
          setError(undefined);
          fetchJson<TrainingAssignmentResponse>(config, session, `/v1/training/assignments/${a.id}/withdraw`, { method: "POST", body: JSON.stringify({ reason: reason.trim() }), headers: { "content-type": "application/json" } })
            .then(() => onWithdrawn(`${a.item.title} withdrawn from ${a.person.displayName}.`))
            .catch((err: unknown) => setError(err))
            .finally(() => setBusy(false));
        }}
      >
        <div className="grid gap-1.5">
          <label htmlFor={reasonId} className={labelClass}>
            Reason
          </label>
          <p id={reasonDescId} className="text-sm text-muted">
            Required — for example, moved to the next cohort. It is kept on the assignment and visible to program staff; the audit trail records only that a reason was given.
          </p>
          <textarea id={reasonId} aria-describedby={reasonDescId} className={inputClass} rows={3} maxLength={500} required value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        <div>
          <Button type="submit" variant="danger" disabled={busy || !reason.trim()}>
            {busy ? "Withdrawing…" : "Withdraw it"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
